import {
    IonContent,
    IonHeader,
    IonPage,
    IonTitle,
    IonToolbar,
    IonButtons,
    IonBackButton,
    IonCardHeader,
    IonCardTitle,
    IonCardContent,
    IonList,
    IonItemDivider,
    IonItem,
    IonCard,
    IonLabel,
    IonButton, useIonAlert, IonProgressBar, IonIcon, IonSpinner
} from '@ionic/react';
import React, {memo} from "react";
import "./AccessGuide.css";
import IWDevice from "../client/IWDevice";
import {isPlatform} from '@ionic/react';
import {useCurrentUserQuery, useGoogleLoginMutation, useLogoutMutation} from "../query/currentUserQuery";
import {useDeviceTokenQuery, usePushEnvironmentQuery} from "../query/deviceInfoQuery";
import {useGatewayConnectionStateQuery} from "../query/gatewayConnectionStateQuery";
import {loginWithGoogle, logoutFromGoogle} from "../client/IWGoogleAuth";
import {logoGoogle} from "ionicons/icons";

const Settings: React.FC = () => {
    const {data: user} = useCurrentUserQuery();
    const {data: deviceToken} = useDeviceTokenQuery();
    const {data: pushEnvironment} = usePushEnvironmentQuery();
    const {isConnecting} = useGatewayConnectionStateQuery();
    const googleLoginMutation = useGoogleLoginMutation();
    const logoutMutation = useLogoutMutation();
    const platform = IWDevice.getPlatforms().join(',');
    const [presentAlert] = useIonAlert();
    const authPending = googleLoginMutation.isPending || logoutMutation.isPending;

    function getErrorMessage(error: unknown) {
        if (typeof error === 'object' && error !== null && 'message' in error) {
            return String((error as {message: unknown}).message);
        }

        return 'Please try again.';
    }

    async function presentAuthenticationError(error: unknown, header = 'Google Login Failed') {
        if (typeof error === 'object' && error !== null && 'code' in error
            && (error as {code?: unknown}).code === 'USER_CANCELLED') {
            return;
        }

        await presentAlert({
            header,
            message: getErrorMessage(error),
            buttons: ['OK']
        });
    }

    async function login() {
        try {
            const idToken = await loginWithGoogle();
            await googleLoginMutation.mutateAsync(idToken);
        } catch (error) {
            await presentAuthenticationError(error);
        }
    }

    async function logout() {
        try {
            await logoutMutation.mutateAsync();

            try {
                await logoutFromGoogle();
            } catch (error) {
                console.warn('(Settings.logout) The broker session ended, but Google logout failed', error);
            }
        } catch (error) {
            await presentAuthenticationError(error, 'Log Out Failed');
        }
    }

    async function revealAlarmCode() {
        return presentAlert({
            header: 'Alarm Code',
            subHeader: 'Your personal alarm code:',
            message: `Code: <strong>${user?.alarmCode?.code ?? ''}</strong>`,
            buttons: ['OK'],
        });
    }

    return (
        <IonPage>
            <IonHeader translucent={isPlatform('capacitor')}>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonBackButton defaultHref="../"/>
                    </IonButtons>
                    <IonTitle>Settings</IonTitle>
                    {isConnecting && <IonProgressBar type="indeterminate"/>}
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen={true}>
                <IonHeader collapse="condense">
                    <IonToolbar>
                        <IonTitle size="large">Settings</IonTitle>
                    </IonToolbar>
                </IonHeader>

                <IonCard color={user?.id ? "success" : "warning"}>
                    <IonCardHeader>
                        <IonCardTitle>{user?.id ? `Welcome, ${user.name}` : 'Not signed in'}</IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                        {user?.id
                            ? `Signed in as ${user.email || user.name}.`
                            : 'Sign in with an approved Google account to access Innovation Way.'}
                    </IonCardContent>
                </IonCard>

                <IonList>
                    <IonItemDivider>Authentication</IonItemDivider>
                    <IonItem>
                        <IonLabel>
                            <h2>{user?.id ? 'Account' : 'Google Login'}</h2>
                            <p>{user?.id ? (user.email || user.name) : 'Use your work account or an existing account.'}</p>
                        </IonLabel>
                        {user?.id
                            ? <IonButton slot="end" fill="outline" color="danger" onClick={logout} disabled={authPending}>
                                {authPending ? <IonSpinner name="crescent"/> : 'Log out'}
                            </IonButton>
                            : <IonButton slot="end" onClick={login} disabled={authPending || isConnecting}>
                                {authPending
                                    ? <IonSpinner name="crescent"/>
                                    : <><IonIcon slot="start" icon={logoGoogle}/>Log in</>}
                            </IonButton>}
                    </IonItem>

                    <IonItemDivider>User Information</IonItemDivider>
                    <IonItem>
                        <IonLabel>Device Token</IonLabel>
                        {deviceToken}
                    </IonItem>
                    <IonItem>
                        <IonLabel>User ID</IonLabel>
                        {user?.id}
                    </IonItem>
                    <IonItem>
                        <IonLabel>Email</IonLabel>
                        {user?.email}
                    </IonItem>
                    <IonItem>
                        <IonLabel>Name</IonLabel>
                        {user?.name}
                    </IonItem>

                    <IonItemDivider>Alarm Access</IonItemDivider>
                    <IonItem>
                        <IonLabel>Status</IonLabel>
                        {user?.alarmCode?.code ? 'Configured' : 'Not Configured'}
                    </IonItem>
                    <IonItem>
                        <IonLabel>Alarm Code ID</IonLabel>
                        {user?.alarmCode?.id || '-'}
                    </IonItem>
                    <IonItem>
                        <IonLabel>Alarm Code</IonLabel>
                        {user?.alarmCode?.code
                            ? <IonButton onClick={revealAlarmCode}>Reveal Code</IonButton>
                            : '-'}
                    </IonItem>

                    <IonItemDivider>Device</IonItemDivider>
                    <IonItem>
                        <IonLabel>Platform</IonLabel>
                        {platform}
                    </IonItem>

                    <IonItemDivider>Push Notifications</IonItemDivider>
                    <IonItem>
                        <IonLabel>Push Environment</IonLabel>
                        {pushEnvironment}
                    </IonItem>
                </IonList>
            </IonContent>
        </IonPage>
    );
};

export default memo(Settings);
